import { type ReactElement } from 'react'
import { isEmpty, messageFromError } from '@terros/common'
import { Stack } from '@mantine/core'
import { MAX_PERMISSION_COMPARE_ENTITIES, useTranslation } from '@front/model'
import { NotReady, Page } from '@components'
import { useCompareEntitySelection } from './useCompareEntitySelection'
import { PermissionComparisonTable } from './PermissionComparisonTable'
import { CompareEntityPicker } from './CompareEntityPicker'

export const ComparePermissions = (): ReactElement => {
  const { t } = useTranslation()
  const {
    entityIds,
    selectedEntities,
    setEntityIds,
    roleOptions,
    isLoadingRoleOptions,
    isLoadingEntities,
    entitiesError,
    roleAccess,
  } = useCompareEntitySelection()

  if (roleAccess.isLoading) return <NotReady id={t('role permissions')} inline isLoading />

  if (roleAccess.error) return <NotReady error={messageFromError(roleAccess.error)} inline />

  if (roleAccess.isDenied) return <NotReady error={t('Role permissions are not available for your account.')} inline />

  return (
    <Stack className='w-0 max-w-full min-w-full'>
      <CompareEntityPicker
        entitiesError={entitiesError}
        entityIds={entityIds}
        isLoadingEntities={isLoadingEntities}
        isLoadingRoleOptions={isLoadingRoleOptions}
        roleOptions={roleOptions}
        selectedEntities={selectedEntities}
        onChange={setEntityIds}
      />
      {isEmpty(selectedEntities) ? (
        <Page.Hero
          description={t('Choose up to {{count}} users, teams, or roles to compare their effective permissions.', {
            count: MAX_PERMISSION_COMPARE_ENTITIES,
          })}
          icon='compare_arrows'
          title={t('Select entities to compare')}
        />
      ) : (
        <PermissionComparisonTable selectedEntities={selectedEntities} />
      )}
    </Stack>
  )
}
